<template>
    <div :class="['w-full', groupClass]" :id="id + '_group'">
        <slot name="prev"></slot>

        <label
            v-if="label"
            :for="id"
            v-html="label"
            :class="[
                'wf-label',
                labelClass,
                { 'wf-label-error': !!error },
                { 'text-green-600': isValid && !error },
            ]"
        ></label>

        <slot name="inputGroup">
            <div class="relative flex items-stretch">
                <!-- Prepend -->
                <div
                    v-if="iconLeft || (iconSide === 'prepend' && icon)"
                    @click="handleIconLeftClick"
                    class="px-3 flex items-center justify-center bg-white border border-r-0 border-gray-100 rounded-l-2xl text-gray-400 group-focus-within:border-primary/20 transition-colors duration-200 cursor-pointer hover:bg-gray-50"
                >
                    <i v-if="iconLeft" :class="iconLeft"></i>
                    <i v-else-if="icon" :class="icon"></i>
                </div>

                <div
                    :class="[
                        'relative flex-1 flex flex-col transition-all duration-200',
                        !unstyled
                            ? 'min-h-[40px] justify-center bg-white border border-gray-200 rounded-2xl focus-within:border-secondary focus-within:ring-4 focus-within:ring-secondary/10'
                            : '',
                        !unstyled && dropdownOpen
                            ? 'rounded-b-none border-b-transparent z-20'
                            : '',
                        !unstyled &&
                        (iconLeft || (iconSide === 'prepend' && icon))
                            ? 'rounded-none'
                            : !unstyled
                              ? 'rounded-2xl'
                              : '',
                        !unstyled &&
                        (iconRight || (iconSide === 'append' && icon))
                            ? 'rounded-none border-r-0'
                            : !unstyled
                              ? 'rounded-2xl'
                              : '',
                        {
                            'border-red-400 ring-4 ring-red-500/10':
                                !!error && !unstyled,
                            'border-green-400 ring-4 ring-green-500/10':
                                isValid && !error && !unstyled,
                            'bg-gray-50 cursor-not-allowed opacity-60':
                                disabled,
                        },
                    ]"
                >
                    <slot name="beforeInput"></slot>
                    <div :class="['w-full', inputContainerClass]">
                        <slot name="input" v-bind="{ ...$props }">
                            <input
                                :type="type"
                                :name="name || false"
                                :id="id"
                                :class="[
                                    'w-full bg-transparent px-3 py-2 text-[15px] font-normal text-gray-900 placeholder:text-gray-500 focus:outline-none !outline-none !border-none !ring-0 rounded-2xl disabled:text-gray-500 disabled:cursor-not-allowed',
                                    inputClass,
                                ]"
                                autocomplete="off"
                                :placeholder="placeholder"
                                :required="required"
                                :readonly="readonly"
                                :disabled="disabled"
                                @click="onClick"
                                @focus="onFocus"
                                @blur="onBlur"
                                @keyup="onKeyUp"
                                @keydown="onKeyDown"
                                @keyPress="onKeyPress"
                                v-model="inputValue"
                                :value="inputValue"
                                ref="input"
                            />
                        </slot>
                    </div>
                    <slot name="inputContainer"></slot>
                </div>

                <!-- Append -->
                <div
                    v-if="iconRight || (iconSide === 'append' && icon)"
                    @click="handleIconRightClick"
                    class="px-3 flex items-center justify-center bg-white border border-l-0 border-gray-100 rounded-r-2xl text-gray-400 group-focus-within:border-primary/20 transition-colors duration-200 cursor-pointer hover:bg-gray-50"
                >
                    <i v-if="iconRight" :class="iconRight"></i>
                    <i v-else-if="icon" :class="icon"></i>
                </div>
            </div>
        </slot>

        <!-- Error -->
        <transition
            enter-active-class="transition ease-out duration-200"
            enter-from-class="opacity-0 -translate-y-2"
            enter-to-class="opacity-100 translate-y-0"
            leave-active-class="transition ease-in duration-150"
            leave-from-class="opacity-100 translate-y-0"
            leave-to-class="opacity-0 -translate-y-2"
        >
            <div v-if="error" class="wf-error">
                <svg
                    class="w-3.5 h-3.5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                >
                    <path
                        fill-rule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                        clip-rule="evenodd"
                    />
                </svg>
                <span v-html="error"></span>
            </div>
        </transition>

        <!-- Success Message -->
        <transition
            enter-active-class="transition ease-out duration-200"
            enter-from-class="opacity-0 -translate-y-2"
            enter-to-class="opacity-100 translate-y-0"
            leave-active-class="transition ease-in duration-150"
            leave-from-class="opacity-100 translate-y-0"
            leave-to-class="opacity-0 -translate-y-2"
        >
            <div v-if="isValid && !error && successMessage" class="wf-success">
                <svg
                    class="w-3.5 h-3.5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                >
                    <path
                        fill-rule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clip-rule="evenodd"
                    />
                </svg>
                <span v-html="successMessage"></span>
            </div>
        </transition>

        <div style="display: none">
            <slot name="hidden"></slot>
        </div>

        <slot name="next"></slot>
    </div>
</template>

<script>
import mixinInput from "./mixinInput.vue";
import mixinCommon from "./mixinCommon.vue";
import { uniqueId } from "lodash";

export default {
    mixins: [mixinCommon, mixinInput],
    props: {
        label: {
            type: String,
            default: "",
        },
        labelClass: {
            type: String,
            default: "",
        },
        iconSide: {
            type: String,
            default: "append",
        },
        icon: {
            type: String,
            default: "",
        },
        iconLeft: {
            type: String,
            default: "",
        },
        iconRight: {
            type: String,
            default: "",
        },
        unstyled: {
            type: Boolean,
            default: false,
        },
        dropdownOpen: {
            type: Boolean,
            default: false,
        },

        onIconLeftClick: {
            type: Function,
            default: () => {},
        },
        onIconRightClick: {
            type: Function,
            default: () => {},
        },

        iconText: {
            type: String,
            default: "",
        },
        error: {
            type: String,
            default: "",
        },
        isValid: {
            type: Boolean,
            default: false,
        },
        successMessage: {
            type: String,
            default: "",
        },
        type: {
            type: String,
            default: "text",
        },
        name: {
            type: String,
            default: "",
        },
        groupClass: {
            type: String,
            default: "",
        },
        inputContainerClass: {
            type: String,
            default: "",
        },
        inputClass: {
            type: String,
            default: "",
        },
        id: {
            type: String,
            default: uniqueId("elm_"),
        },
        placeholder: {
            type: String,
            default: "",
        },
        readonly: null,
        disabled: null,
        required: null,

        onClick: {
            type: Function,
            default: () => {},
        },
        onFocus: {
            type: Function,
            default: () => {},
        },
        onBlur: {
            type: Function,
            default: () => {},
        },
        onKeyDown: {
            type: Function,
            default: () => {},
        },
        onKeyUp: {
            type: Function,
            default: () => {},
        },
        onKeyPress: {
            type: Function,
            default: () => {},
        },
    },
    data() {
        return {
            inputSpaceX: "", // No longer needed as we use Tailwind flex
        };
    },
    methods: {
        reset() {
            this.inputValue = this.value + "";
        },
        checkIsValid() {
            return !this.error;
        },
        handleIconLeftClick() {
            if (
                this.onIconLeftClick &&
                this.onIconLeftClick.name !== "default"
            ) {
                this.onIconLeftClick();
            } else {
                this.focus();
            }
        },
        handleIconRightClick() {
            if (
                this.onIconRightClick &&
                this.onIconRightClick.name !== "default"
            ) {
                this.onIconRightClick();
            } else {
                this.focus();
            }
        },
        focus() {
            if (this.$refs.input) {
                this.$refs.input.focus();
            }
        },
    },
};
</script>
